
import { ImagePlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { createLogger } from "@/utils/logger";

const logger = createLogger('ImageUploader');

interface ImageUploaderProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
}

export const ImageUploader = ({ images, onImagesChange }: ImageUploaderProps) => {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const uploadImage = async (file: File) => {
    try {
      // Sanitize filename to avoid issues with spaces or special characters
      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      
      logger.debug('Uploading image:', {
        fileName,
        fileSize: file.size,
        fileType: file.type
      });

      // Try to upload to the profile-photos bucket which we know exists
      const { error: uploadError, data } = await supabase.storage
        .from('profile-photos')
        .upload(`meals/${fileName}`, file, {
          contentType: file.type,
          upsert: true
        });

      if (uploadError) {
        logger.error('Storage upload error:', uploadError);
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      logger.debug('Upload successful');
      
      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile-photos')
        .getPublicUrl(`meals/${fileName}`);
      
      return publicUrl;
    } catch (error: any) {
      logger.error('Error in uploadImage function:', error);
      throw error;
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setErrorMessage(null);
    
    try {
      // Validate files first
      for (const file of Array.from(files)) {
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`File "${file.name}" exceeds the 5MB size limit`);
        }
        
        if (!file.type.startsWith('image/')) {
          throw new Error(`File "${file.name}" is not a valid image`);
        }
      }
      
      // Then upload each file
      const uploadPromises = Array.from(files).map(file => uploadImage(file));
      const uploadedUrls = await Promise.all(uploadPromises);
      
      // Update the images state
      onImagesChange([...images, ...uploadedUrls]);
      logger.debug(`Successfully uploaded ${uploadedUrls.length} images`);
    } catch (error: any) {
      logger.error('Error uploading images:', error);
      setErrorMessage(error.message || "Failed to upload images. Please try again.");
      toast({
        title: "Upload Error",
        description: error.message || "Failed to upload images. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      // Clear the input value to allow uploading the same file again
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    onImagesChange(images.filter((_, i) => i !== index));
    logger.debug(`Removed image at index ${index}`);
  };

  return (
    <div className="space-y-4">
      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
      
      <div className="flex flex-wrap gap-4">
        {images.map((image, index) => (
          <div key={index} className="relative">
            <img
              src={image}
              alt={`Meal preview ${index + 1}`}
              className="w-24 h-24 object-cover rounded-lg"
            />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
              onClick={() => handleRemoveImage(index)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <label className={`w-24 h-24 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}>
          <input
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
            disabled={isUploading}
          />
          {isUploading ? (
            <div className="h-8 w-8 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <ImagePlus className="h-8 w-8 text-gray-400" />
          )}
        </label>
      </div>
    </div>
  );
};
